<template>
    <div class="cmp-signin td-card white border rounded td-mx-auto">
        <h2 v-if="stage == 'login' || stage == 'register-start' || stage == 'register'" class="td-h3 td-text-center">{{ heading }}</h2>
        <div v-if="stage == 'invite'">
            <h2 class="td-h3 td-text-center">You've been invited!</h2>
            <p>{{ userInvite.firstName }} {{ userInvite.lastName }},</p>
            <p>You have been invited to create a TruckDown user account that will be associated with an existing TruckDown account: <strong>{{ userInvite.accountName }}</strong></p>
            <p><strong>The final step is to set a password for your account.</strong></p>
        </div>
        <div v-if="stage != 'invite' && inviteId && inviteId != ''" class="td-banner no-icon danger border my-5">
            <h4>We Couldn't Find Your Invite</h4>
            <p>The Invite Code you are signing up with appears to be invalid, has expired, or was removed by an account administrator.</p>
            <br />
            <p><strong>Please reach out to the account administrator that invited you.</strong></p>
        </div>
        <label>{{usernameLabel}}</label>
        <div v-if="stage != 'invite' && stage != 'register'">
            <AddressValidator :reset="usernameResetTrigger"
                              :disable-validation="usePassword"
                              :validation-failed="loginErrorMessages.length > 0"
                              :enable-username="true" v-model="username"
                              :with-password="isLoggingInWithPassword"
                              :disable-focus="disableFocus"
                              @validated="validateUsername"
                              @choosePassword="showLoginWithPassword"
                              placeholder="Enter email / Mobile number">
            </AddressValidator>
        </div>
        <div v-if="stage == 'register'">
            <input type="text" :value="username" :disabled="true" class="td-bg-color" />
        </div>
        <div v-if="stage == 'invite'">
            <input type="text" :value="userInvite.username" :disabled="true" class="td-bg-color" />
        </div>

        <template v-if="stage == 'login'">
            <div class="login-form">
                <template v-if="isLoggingInWithPassword">
                    <label>Password</label>
                    <input :class="{'error' : loginErrorMessages.length > 0}"
                           name="password"
                           type="password"
                           v-model="password"
                           placeholder="Password"
                           ref="passwordctrl"
                           required
                           @keydown.enter="loginWithPassword" />
                    <div class="td-banner danger mt-1 slim" v-if="loginErrorMessages.length > 0 && isLoggingInWithPassword">
                        <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                        <p v-for="message in loginErrorMessages"> {{ message }}</p>
                    </div>
                    <button class="td-btn primary mt-2 td-full-width" @click="loginWithPassword">
                        <span v-if="!isInProgress">Login</span>
                        <span v-if="isInProgress">
                            <icon class="icon" type="solid" name="faSpinner" aria-hidden="true" spin></icon>
                        </span>
                    </button>
                </template>
                <template v-if="!isInProgress">
                    <a class="td-link no-underline td-text-center my-2 td-flex" v-if="isLoggingInWithPassword" href="#" @click.prevent="showLoginWithCode"><strong>Forgot Password? Send a Login Code instead.</strong></a>
                </template>
            </div>
        </template>

        <template v-if="stage == 'inactive'">
            <div class="td-banner warning slim">
                <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                <div>There is an issue with the account you entered.  Please <a href="/contact">contact us</a> for more information.</div>
            </div>
        </template>

        <template v-if="stage == 'register-start'">
            <div class="td-banner info slim mb-2" v-if="infoMessage">
                <icon class="icon" type="solid" name="faUserPlus" aria-hidden="true"></icon>
                <p>{{ infoMessage }}</p>
            </div>
            <label>{{ alternatePlaceholder }}</label>
            <AddressValidator :reset="alternateUsernameResetTrigger"
                              :enable-username="false"
                              v-model="alternateUsername"
                              @validated="validateAlternate"
                              @skip="skipAlternate"
                              :disable-email="usernameType == 'email' || stage == 'invite'"
                              :disable-sms="usernameType == 'sms'"
                              :disable-password="true"
                              :allow-skip="usernameType == 'email' || stage == 'invite'"
                              :placeholder="alternatePlaceholder"></AddressValidator>

            <div class="mb-2 checkboxes">
                <label class="td-checkbox-label">
                    <i class="td-text-subdued">By checking this box, you agree to receive customer care text messages from TruckDown regarding your account and support requests.  Message frequency varies.  Message and data rates may apply.  Reply HELP for help and STOP to cancel.  Consent is not required to create an account or use TruckDown services.</i>
                    <input type="checkbox" v-model="mobileOptin" />
                    <span class="mark"></span>
                </label>

                <div class="td-banner danger slim mt-2" v-if="showMobileOptinError">
                    <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                    <p>By providing your mobile number, you must consent to receive customer care text messages from TruckDown.  If you do not wish to receive these messages, remove your mobile number.</p>
                </div>

                <label class="td-checkbox-label">
                    <i class="td-text-subdued">By checking this box, you accept our <a href="/terms">terms & conditions</a> and our <a href="/terms/privacy">privacy policy</a>.</i>
                    <input type="checkbox" v-model="terms" />
                    <span class="mark"></span>
                </label>
                <div class="td-banner danger slim mt-2" v-if="showMobileTermsError">
                    <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                    <p>You must accept our terms & conditions and privacy policy to continue.</p>
                </div>
            </div>

            <div v-if="isAlternateUsernameValidated">
                <button class="td-btn primary td-full-width mt-2" @click.prevent="continueAlternate">
                    <span class="btn-name">Continue</span>
                </button>
            </div>
        </template>

        <template v-if="stage == 'register' || stage == 'invite'">
            <div v-if="stage == 'register' && hasMobile">
                <label>{{ alternatePlaceholder }}</label>
                <input type="text" :value="alternateUsername" :disabled="true" class="td-bg-color" />
            </div>

            <div class="form-row">
                <div>
                    <label>First Name</label>
                    <input required type="text" :class="{'error' : hasValidationError('firstName'), 'td-bg-color' : stage == 'invite'}" placeholder="First Name" v-model="firstName" :disabled="stage == 'invite'" autocomplete="off" />
                    <div class="td-banner danger slim" v-if="hasValidationError('firstName')">
                        <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                        <p>{{ getValidationError('firstName') }}</p>
                    </div>
                </div>
                <div>
                    <label>Last Name</label>
                    <input required type="text" :class="{'error' : hasValidationError('lastName'), 'td-bg-color' : stage == 'invite'}" placeholder="Last Name" v-model="lastName" :disabled="stage == 'invite'" autocomplete="off" />
                    <div class="td-banner danger slim" v-if="hasValidationError('lastName')">
                        <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                        <p>{{ getValidationError('lastName') }}</p>
                    </div>
                </div>
            </div>
            <div class="form-row">
                <div>
                    <label>Contact Phone Number</label>
                    <input required :class="{'error' : hasValidationError('phone')}" type="tel" placeholder="e.g. 999-999-9999" v-model="phone" autocomplete="off" />
                    <div class="td-banner danger slim" v-if="hasValidationError('phone')">
                        <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                        <div> {{ getValidationError('phone') }}</div>
                    </div>
                </div>
                <div>
                    <label>Company Name</label>
                    <input type="text" placeholder="Company Name" v-model="companyName" :disabled="stage == 'invite'" :class="{'td-bg-color' : stage == 'invite'}"  autocomplete="off" />
                </div>
            </div>
            <div class="form-row">
                <div>
                    <label>Password</label>
                    <input required :class="{'error' : hasValidationError('newPassword')}" type="password" placeholder="At least 6 characters" v-model="newPassword" autocomplete="off" />
                    <div class="td-banner danger slim" v-if="hasValidationError('newPassword')">
                        <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                        <p> {{ getValidationError('newPassword') }}</p>
                    </div>
                </div>
                <div>
                    <label>Confirm Password</label>
                    <input required type="password" :class="{'error' : hasValidationError('confirmPassword')}" placeholder="Confirm Password" v-model="confirmPassword" autocomplete="off" />
                    <div class="td-banner danger slim" v-if="hasValidationError('confirmPassword')">
                        <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                        <p> {{ getValidationError('confirmPassword') }}</p>
                    </div>
                </div>
            </div>

            <div class="mb-2 checkboxes">
                <label class="td-checkbox-label">
                    <i class="td-text-subdued">Would you like to see updated service information from us?</i>
                    <input type="checkbox" v-model="marketing" />
                    <span class="mark"></span>
                </label>

                <label class="td-checkbox-label">
                    <i class="td-text-subdued">Do you accept our <a href="/terms">terms and conditions</a> and our <a href="/terms/privacy">privacy policy</a>?</i>
                    <input type="checkbox" v-model="terms" />
                    <span class="mark"></span>
                </label>
                <div class="td-banner danger slim mt-2" v-if="hasValidationError('terms')">
                    <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                    <p> {{ getValidationError('terms') }}</p>
                </div>
            </div>

            <div class="account-type mt-3" v-if="stage != 'invite'">
                <label for="account-type">Account Type</label>
                <select class="td-select mb-1" id="account-type" v-model="selectedAccountType" required>
                    <option>Select Account Type</option>
                    <option class="option" value="vendor">
                        Vendor Account
                    </option>
                    <option class="option" value="free">
                        Free Account
                    </option>
                    <option class="option" value="fleet">
                        Fleet Account
                    </option>
                </select>
                <div class="td-banner danger slim" v-if="hasValidationError('selectedAccountType')">
                    <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                    <p>{{ getValidationError('selectedAccountType') }}</p>
                </div>
                <div class="td-banner info" v-if="selectedAccountType === 'vendor'">
                    <icon class="icon" type="solid" name="faInfoCircle" aria-hidden="true"></icon>
                    <div>Create a <strong>Vendor Account </strong>if you want to advertise your business.</div>
                </div>
                <div class="td-banner info" v-else-if="selectedAccountType === 'free'">
                    <icon class="icon" type="solid" name="faInfoCircle" aria-hidden="true"></icon>
                    <div>Create a <strong>Free Account</strong> if you want to search for services, rate vendors, and/or build routes.</div>
                </div>
                <div class="td-banner info" v-else-if="selectedAccountType === 'fleet'">
                    <icon class="icon" type="solid" name="faInfoCircle" aria-hidden="true"></icon>
                    <p>Create a <strong>Fleet Account</strong> if you are responsible for a large fleet and want more control over the vendors you use.</p>
                </div>
                <div class="td-banner info no-icon" v-else>
                    <p class="mb-1">Create a <strong>Vendor Account </strong>if you want to advertise your business.</p>
                    <p class="mb-1">Create a <strong>Free Account</strong> if you want to search for services, rate vendors, and/or build routes.</p>
                    <p>Create a <strong>Fleet Account</strong> if you are responsible for a large fleet and want more control over the vendors you use.</p>
                </div>
            </div>

            <template v-if="isInProgress">
                <div class="td-banner slim">
                    <icon class="icon" type="solid" name="faSpinner" aria-hidden="true" spin></icon>
                    <p>Please wait</p>
                </div>
            </template>
            <template v-else>
                <button class="td-btn primary td-full-width mt-2" @click.prevent="createAccount(selectedAccountType)">
                    <span class="btn-name">Create Account</span>
                </button>
                <div class="td-banner danger slim mt-1" v-if="hasValidationError('')">
                    <icon class="icon" type="solid" name="faTriangleExclamation" aria-hidden="true"></icon>
                    <div> {{ getValidationError('') }}</div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
    import script from './CreateOrSignin';
    export default script;</script>

<style lang="less">
    @import "./CreateOrSignin.less";
</style>
